import DownloadSVG from '@public/icons/download.svg';
import MobileScrollSVG from '@public/icons/mobile-scroll-back.svg';
import PdfSVG from '@public/icons/pdf.svg';
import ArrSVG from '@public/icons/small-arrow.svg';
import { AnimatePresence, m as motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef, useState } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { Document } from '@/entities/document';
import { TeamPerson } from '@/entities/teamPerson';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getEmployees } from '@/shared/api/employees';
import { getHistory } from '@/shared/api/history';
import { getPageDetail } from '@/shared/api/page-detail';
import { getQuestions } from '@/shared/api/questions';
import { getReviews } from '@/shared/api/reviews';
import { getSiteSettings } from '@/shared/api/site-settings';
import { getVideo } from '@/shared/api/video';
import { useActiveSectionValue } from '@/shared/atoms/active-section';
import { useHeaderAnchorsEffect } from '@/shared/atoms/header-anchors';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { PageDataState } from '@/shared/atoms/page-data';
import MapSection from '@/shared/components/MapBlock/MapSection';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import ConstructorSection, { ConstructorSectionProps } from '@/shared/components/sections/ConstructorSection';
import ExpoDocumentsSection, { ExpoDocumentsSectionProps } from '@/shared/components/sections/ExpoDocumentsSection';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import PartnersSection, { PartnersSectionProps } from '@/shared/components/sections/PartnersSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import ReviewsSection, { ReviewsSectionProps } from '@/shared/components/sections/ReviewsSection';
import Button from '@/shared/components/shared/Button';
import Dropdown from '@/shared/components/shared/Dropdown';
import GradientBG from '@/shared/components/shared/GradientBG';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import Reveal from '@/shared/components/shared/Reveal';
import Typografed from '@/shared/components/shared/Typografed';
import VideoReveal, { VideoRevealProps } from '@/shared/components/shared/VideoReveal';
import { isPdf } from '@/shared/hooks/use-form-sender/validator/utils';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { computeEventDatesFromDays } from '@/shared/lib/utils/event-page-json-ld';
import { generateExpoJsonLd } from '@/shared/lib/utils/expo-json-ld';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import DraftSection, { DraftSectionProps } from '@/shared/ui/sections/DraftSection';

export type DocumentsDropdownBtnProps = {
    title: string;
    data: Document[];
};

const ExpoPage = ({
    meta,
    breadcrumbs,
    h1,
    pageData,
    documentsDropdownBtnProps,
    constructorSectionProps,
    draftsSectionProps,
    partnersSectionProps,
    howItWasSectionProps,
    reviewsSectionProps,
    videoRevealProps,
    questionsSectionProps,
    contactPersonProps,
    faqSectionProps,
    eventStartDateIso,
    eventEndDateIso,
    footerData,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const activeSection = useActiveSectionValue();
    const [isButtonVisible, setIsButtonVisible] = useState(false);

    const { scrollYProgress } = useScroll({ target: wrapperRef, offset: ['start -30%', 'end end'] });
    useMotionValueEvent(scrollYProgress, 'change', (progress) => {
        setIsButtonVisible(progress > 0);
    });

    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const theme = useHeaderThemeValue();

    useHeaderAnchorsEffect({ anchors: [], navigation: [], scrollYProgress });
    const renderContent = () => {
        switch (activeSection) {
            case 0:
                return (
                    <>
                        <ConstructorSection constructorSectionProps={constructorSectionProps} theme="dark">
                            <div className="index-hero__expo-content">
                                <div className="index-hero__descr text-s">
                                    <Typografed>
                                        Скачайте все необходимые документы, касающиеся организации и проведения
                                        выставки, а также правила и условия участия.
                                    </Typografed>
                                </div>

                                {documentsDropdownBtnProps && (
                                    <Dropdown className={'header-btn expo-docs-btn middle-out'}>
                                        <Dropdown.Toggler className="header-btn__toggler">
                                            <span className="text-xs">{documentsDropdownBtnProps.title}</span>
                                            <ArrSVG className="header-arrow-svg" />
                                        </Dropdown.Toggler>
                                        <Dropdown.Content className="header-btn__content" position="bottom-right">
                                            <ul className="list-unstyled menu-dropdown-list">
                                                {documentsDropdownBtnProps.data.map((doc, i) => (
                                                    <Dropdown.Picker
                                                        tag={'li'}
                                                        className="dropdown-list__item expo-dropdown-list__item"
                                                        key={i}
                                                    >
                                                        <a
                                                            className="menu-dropdown-link expo-menu-dropdown-link text-xs"
                                                            href={doc.src}
                                                            target={isPdf(doc.src) ? '_blank' : undefined}
                                                            rel={isPdf(doc.src) ? 'noreferrer noopener' : undefined}
                                                            download={isPdf(doc.src) ? undefined : true}
                                                        >
                                                            <div className="dropdown-list__item-wrapper">
                                                                <PdfSVG className="pdf-svg expo-pdf-svg" />
                                                                <span className="menu-dropdown-link__text">
                                                                    {doc.title}
                                                                </span>
                                                            </div>

                                                            <DownloadSVG className="download-svg" />
                                                        </a>
                                                    </Dropdown.Picker>
                                                ))}
                                            </ul>
                                        </Dropdown.Content>
                                    </Dropdown>
                                )}
                            </div>
                        </ConstructorSection>
                        <AnimatePresence>
                            {isButtonVisible && (
                                <motion.div
                                    exit={{ opacity: 0 }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className={`scroll-back-btn-mobile theme-${theme}`}
                                >
                                    <MobileScrollSVG />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                );
            case 1:
                return <></>;
            default:
                return null;
        }
    };

    return (
        <DefaultLayout currentPageId={PAGES.EXPO} ref={wrapperRef} className="expo-page">
            <script
                type="application/json"
                className="js-page-data-json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(pageData) }}
            ></script>

            <AnimatePresence>
                {isButtonVisible && (
                    <motion.div
                        exit={{ opacity: 0 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`scroll-back-btn-mobile theme-${theme}`}
                    >
                        <MobileScrollSVG />
                    </motion.div>
                )}
            </AnimatePresence>

            <GradientBG scrollYProgress={scrollYProgress} />

            <section className="expo-section-wrapper expo-first__section js-header-theme-trigger" data-theme="light">
                <div className="expo-video__wrapper">
                    <VideoReveal videoRevealProps={videoRevealProps} />
                    <Reveal instant={true} delay={0}>
                        <div className="expo-video__wrapper-head">
                            <h1 className="expo-video__title">{h1}</h1>

                            <Button
                                tag={Link}
                                href={'/participation/visitors/#feedback'}
                                className="free-events-section-btn"
                                variant="outline-light"
                            >
                                Забронировать стенд
                            </Button>
                        </div>
                    </Reveal>
                </div>

                {draftsSectionProps.images.length > 0 && <DraftSection data={draftsSectionProps} />}
            </section>
            <MapSection id="constructor" data-theme="dark" className="js-header-theme-trigger" />
            {/* {renderContent()} */}

            {documentsDropdownBtnProps.data.length > 0 && (
                <ExpoDocumentsSection
                    className="js-header-theme-trigger"
                    data-theme="dark"
                    expoDocumentsSectionProps={documentsDropdownBtnProps}
                />
            )}

            <PartnersSection partnersSectionProps={partnersSectionProps} isRenderWithPopup={true} theme="dark" />
            {howItWasSectionProps.slider.images.length > 0 && (
                <HowItWasSection howItWasSectionProps={howItWasSectionProps} theme="dark" />
            )}
            <ReviewsSection
                reviewsSectionProps={reviewsSectionProps}
                className="js-header-theme-trigger"
                data-theme="dark"
                id="reviews"
            />
            <QuestionsSection questionsSectionProps={questionsSectionProps} contactPersonProps={contactPersonProps} />
            {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                <FaqSection faqSectionProps={faqSectionProps} />
            )}
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-expo"
                data={generateExpoJsonLd({
                    metaDescription: meta.description,
                    h1,
                    draftsDescriptionHtml: draftsSectionProps.text,
                    imageSrc: videoRevealProps.poster?.src ?? draftsSectionProps.images[0]?.src ?? null,
                    eventStartDateIso,
                    eventEndDateIso,
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default ExpoPage;

type ExpoPageProps = NonUndefined<
    CommonPageProps & {
        eventStartDateIso: string | null;
        eventEndDateIso: string | null;
        h1: string;
        pageData: PageDataState;
        draftsSectionProps: DraftSectionProps;
        constructorSectionProps: ConstructorSectionProps;
        partnersSectionProps: PartnersSectionProps;
        howItWasSectionProps: HowItWasSectionProps;
        reviewsSectionProps: ReviewsSectionProps;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        faqSectionProps: FaqSectionProps;
        videoRevealProps: VideoRevealProps;
        documentsDropdownBtnProps: DocumentsDropdownBtnProps;
        expoDocumentsSectionProps: ExpoDocumentsSectionProps;
    }
>;

export const getStaticProps: GetStaticProps<ExpoPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'expo' } });

    const [
        commonPageProps,
        companiesResponse,
        reviewsResponse,
        historyResponse,
        questionsResponse,
        employeesResponse,
        videoResponse,
        daysResponse,
        siteSettings,
    ] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.EXPO),
        getCompanyCategories({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }).then((categoriesResponse) => {
            if (Array.isArray(categoriesResponse.data) && categoriesResponse.data.length === 0) {
                return Promise.resolve<AppCompaniesResponse>({
                    status: 'success',
                    data: [],
                });
            }

            return getCompanies({
                params: { categories: (categoriesResponse.data ?? []).map((category) => category.id) },
            });
        }),
        getReviews({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getHistory({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getQuestions({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getVideo({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getDays({ params: { page: pageResponse.data?.id ?? PAGES.EXPO } }),
        getSiteSettings({
            params: {
                keys: [
                    'expo_first_screen_title',
                    'expo_first_screen_description',
                    'expo_first_screen_video',
                    'expo_first_screen_images',

                    'expo_constructor_title',
                    'expo_constructor_button_text',
                    'expo_constructor_documents',

                    'expo_company_title',

                    'expo_history_title',
                    'expo_history_description',
                    'expo_history_link_text',
                    'expo_history_link',

                    'expo_review_title',
                    'expo_review_description',

                    'expo_form_title',
                    'expo_form_description',

                    'expo_faq_title',
                ],
            },
        }),
    ]);

    const { startDateIso: eventStartDateIso, endDateIso: eventEndDateIso } = computeEventDatesFromDays(
        daysResponse.data ?? [],
    );

    return {
        props: {
            ...commonPageProps,
            eventStartDateIso: eventStartDateIso ?? null,
            eventEndDateIso: eventEndDateIso ?? null,
            bodyClass: 'expo-page expo',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Выставка', href: '/expo/' },
            ],
            pageData: {
                pageId: pageResponse.data?.id ?? -1,
            },
            h1: siteSettings.data?.expo_first_screen_title || 'TNF EXPO',
            partnersSectionProps: {
                title: siteSettings.data?.expo_company_title || 'Организаторы и партнеры',
                buttonText: '',
                partners: (companiesResponse.data ?? []).map((company) => ({
                    logo: company.logo || null,
                    title: company.title ?? '',
                    description: company.description ?? '',
                    href: company.href ?? '',
                    categoryId: company.categoryId ?? 0,
                })),
            },
            constructorSectionProps: {
                h1: siteSettings.data?.expo_constructor_title || 'Выберите <br> свой стенд',
            },
            documentsDropdownBtnProps: {
                title: siteSettings.data?.expo_constructor_button_text || 'Документы TNF EXPO',
                data: (siteSettings.data?.expo_constructor_documents ?? []).map((document) => ({
                    src: document.src ?? '',
                    title: document.title ?? '',
                    alt: document.alt ?? '',
                })),
            },
            expoDocumentsSectionProps: {
                title: siteSettings.data?.expo_constructor_title || 'Документы',
                data: (siteSettings.data?.expo_constructor_documents ?? []).map((document) => ({
                    src: document.src ?? '',
                    title: document.title ?? '',
                    alt: document.alt ?? '',
                })),
            },
            questionsSectionProps: {
                title: siteSettings.data?.expo_form_title ?? 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.expo_form_description ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
            videoRevealProps: {
                desktopVideo: videoResponse.data?.video ?? [],
                mobileVideo: videoResponse.data?.mobileVideo ?? [],
                poster: videoResponse.data?.videoPreview ?? null,
                posterMobile: videoResponse.data?.mobileVideoPreview ?? null,
            },
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: siteSettings.data?.expo_history_title || 'Как было в том году?',
                description:
                    siteSettings.data?.expo_history_description ||
                    'Форум прошлого года стал одним из самых значимых событий нефтегазовой промышленности, объединив в Тюмени ведущих экспертов и ключевых игроков отрасли со всей страны',
                link: siteSettings.data?.expo_history_link
                    ? {
                          href: siteSettings.data?.expo_history_link,
                          name: siteSettings.data?.expo_history_link_text ?? '',
                      }
                    : null,
            },
            reviewsSectionProps: {
                title: siteSettings.data?.expo_review_title || 'О нас говорят',
                subtitle:
                    siteSettings.data?.expo_review_description ||
                    'Форум TNF 2024 стал кульминацией года в нефтегазовой промышленности, объединив ведущих экспертов и ключевых игроков отрасли в одном месте',
                cards: reviewsResponse.data ?? [],
            },
            faqSectionProps: {
                title: siteSettings.data?.expo_faq_title || 'Частые вопросы',
                questions: questionsResponse.data ?? [],
            },
            draftsSectionProps: {
                text:
                    siteSettings.data?.expo_first_screen_description ||
                    'Выставка TNF 2024 - главная точка притяжения вашей целевой аудитории. Продуманное расположение у входа на площадку Форума и традиционный обход ВИП-делегацией гарантирует внимание всех участников: от потенциальных клиентов до СМИ.',
                images: siteSettings.data?.expo_first_screen_images || [
                    { src: '/img/drafts/img-1.png', width: 1, height: 1, alt: '1' },
                    { src: '/img/drafts/img-2.png', width: 1, height: 1, alt: '2' },
                    { src: '/img/drafts/img-3.png', width: 1, height: 1, alt: '3' },
                    { src: '/img/drafts/img-4.png', width: 1, height: 1, alt: '4' },
                    { src: '/img/drafts/img-5.png', width: 1, height: 1, alt: '5' },
                ],
            },
        } satisfies ExpoPageProps,
        revalidate: 60,
    };
};
